
import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import MonthSelector from "@/components/calendar/MonthSelector";
import WeeklyCalendar from "@/components/calendar/WeeklyCalendar";
import CalendarSettingsModal from "@/components/calendar/settings/CalendarSettingsModal";
import CalendarHeader from "@/components/calendar/CalendarHeader";
import CalendarLoading from "@/components/calendar/CalendarLoading";
import { useCalendarSettings } from "@/hooks/use-calendar-settings";
import { useCalendarEvents } from "@/hooks/use-calendar-events";
import { useClientInfo } from "@/hooks/use-client-info";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useWindowActive } from "@/hooks/use-window-active";

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(isMobile ? "day" : "week");
  const navigate = useNavigate();
  const isWindowActive = useWindowActive(); // Rimuoviamo il parametro
  
  const { clientId, isClientView, user } = useClientInfo();

  const {
    calendarSettings,
    isLoading: isSettingsLoading,
    saveCalendarSettings
  } = useCalendarSettings(user, isClientView);

  const {
    calendarEvents,
    isLoading: isEventsLoading,
    addCalendarEvent,
    deleteCalendarEvent,
    error: eventsError,
    pendingRequestsCount,
    refreshPendingRequests,
    approveSessionRequest,
    rejectSessionRequest
  } = useCalendarEvents(currentDate, isClientView, clientId);

  const isLoading = isSettingsLoading || isEventsLoading;

  // Questo useEffect memorizzato per prevenire ricreazioni
  const handleLoadingComplete = useCallback(() => {
    if (!isLoading && !isFullyLoaded) {
      const timer = setTimeout(() => {
        setIsFullyLoaded(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, isFullyLoaded]);

  // Unificato gli effetti correlati allo stato di caricamento
  useEffect(() => {
    // Gestisce lo stato di caricamento
    handleLoadingComplete();
    
    // Reset lo stato quando cambia lo stato di caricamento
    if (isLoading) {
      setIsFullyLoaded(false);
    }
  }, [isLoading, handleLoadingComplete]);

  // Imposta vista mobile - mantiene logica separata perché dipende solo da isMobile
  useEffect(() => {
    if (isMobile) {
      setActiveTab("day");
    }
  }, [isMobile]);

  // Gestione errori - eseguita solo quando l'errore cambia
  useEffect(() => {
    if (!eventsError || !isWindowActive) return; // Non eseguire se non c'è errore o finestra non attiva
    
    if (retryCount < 3) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setIsFullyLoaded(false);
      }, 2000);
      
      toast.error("Errore nel caricamento degli eventi. Nuovo tentativo in corso...");
      
      return () => clearTimeout(timer);
    } else {
      toast.error("Impossibile caricare gli eventi del calendario. Ricarica la pagina.");
    }
  }, [eventsError, retryCount, isWindowActive]);

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const handlePrevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleNavigateToClient = (clientId: string) => {
    navigate(`/admin/clients/${clientId}`);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] animate-fade-in">
      <CalendarHeader 
        startDate={new Date()}
        currentDate={currentDate}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onPrevWeek={handlePrevWeek}
        onNextWeek={handleNextWeek}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        isClientView={isClientView}
        onOpenSettings={() => setIsSettingsOpen(true)}
      />

      <Card className="border rounded-lg shadow-sm flex-1 mt-4 overflow-hidden">
        <CardContent className="p-0 sm:p-2 h-full">
          {!isFullyLoaded ? (
            <CalendarLoading retryCount={retryCount} />
          ) : (
            <div className="h-full flex flex-col">
              <MonthSelector
                currentDate={currentDate}
                onDateChange={setCurrentDate}
              />
              
              <div className="flex-1 overflow-hidden">
                <WeeklyCalendar
                  currentDate={currentDate}
                  onDateChange={setCurrentDate}
                  onOpenSettings={() => setIsSettingsOpen(true)}
                  settings={calendarSettings}
                  events={calendarEvents}
                  onAddEvent={async (eventData) => {
                    await addCalendarEvent(eventData);
                  }}
                  onDeleteEvent={deleteCalendarEvent}
                  isClientView={isClientView}
                  clientId={clientId}
                  pendingRequestsCount={pendingRequestsCount}
                  onRefreshRequests={refreshPendingRequests}
                  onApproveRequest={approveSessionRequest}
                  onRejectRequest={rejectSessionRequest}
                  onNavigateToClient={handleNavigateToClient}
                  activeView={activeTab}
                  onViewChange={handleTabChange}
                />
              </div>
            </div>
          )}
          
          {!isClientView && (
            <CalendarSettingsModal
              open={isSettingsOpen}
              onOpenChange={setIsSettingsOpen}
              settings={calendarSettings}
              onSaveSettings={saveCalendarSettings}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Calendar;
